import React, { useEffect, useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Typography,
  Box,
  Stack,
  useTheme,
  useMediaQuery,
  Collapse,
} from "@mui/material";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useNavigate, useLocation } from "react-router-dom";
import { clearCreateBookingDraft } from "../../pages/booking-management/createBookingSessionStorage";
import { NAVIGATION_ITEMS } from "../../constants";
import type { NavigationItem } from "../../types";
import { useAppDispatch, useAppSelector } from "../../hooks/useAuth";
import { RootState } from "../../store";
import { logoutThunk } from "../../services/auth";
import useRBAC from "../../hooks/useRBAC";
import LogoutIcon from "../../assets/icons/LogoutIcon.svg";
import {
  DashboardIcon,
  UserManagementIcon,
  StaffManagementIcon,
  HubManagementIcon,
  VehicleManagementIcon,
  RentalManagementIcon,
  PaymentManagementIcon,
  BookingManagementIcon,
  SupportSystemIcon,
  RideManagementIcon,
  SettingsIcon,
  SIDEBAR_ICON_ACTIVE,
  SIDEBAR_ICON_INACTIVE,
} from "../../assets/icons/sidebarIcons";

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
  collapsed: boolean;
  onCollapse: () => void;
}

const ICON_MAP: Record<string, (isActive: boolean) => React.ReactElement> = {
  Dashboard: (active) => <DashboardIcon width={22} height={22} isActive={active} />,
  People: (active) => <UserManagementIcon width={20} height={20} isActive={active} />,
  Groups: (active) => <StaffManagementIcon width={22} height={22} isActive={active} />,
  LocationOn: (active) => <HubManagementIcon width={21} height={21} isActive={active} />,
  DirectionsCar: (active) => <VehicleManagementIcon width={25} height={25} isActive={active} />,
  Event: (active) => <RentalManagementIcon width={25} height={25} isActive={active} />,
  Payment: (active) => <PaymentManagementIcon width={22} height={22} isActive={active} />,
  BookOnline: (active) => <BookingManagementIcon width={22} height={22} isActive={active} />,
  Support: (active) => <SupportSystemIcon width={22} height={22} isActive={active} />,
  Ride: (active) => <RideManagementIcon width={26} height={26} isActive={active} />,
  Settings: (active) => <SettingsIcon width={22} height={22} isActive={active} />,
  CardGiftcard: (active) => (
    <CardGiftcardIcon
      sx={{
        fontSize: 22,
        color: active ? SIDEBAR_ICON_ACTIVE : SIDEBAR_ICON_INACTIVE,
      }}
    />
  ),
  CurrencyExchange: (active) => (
    <CurrencyExchangeIcon
      sx={{
        fontSize: 22,
        color: active ? SIDEBAR_ICON_ACTIVE : SIDEBAR_ICON_INACTIVE,
      }}
    />
  ),
};

const getIcon = (name: string, isActive: boolean) =>
  ICON_MAP[name]?.(isActive) ?? <DashboardIcon width={22} height={22} isActive={isActive} />;

const navItemVisible = (item: NavigationItem, canAccess: (path: string) => boolean): boolean => {
  if (item.children?.length) {
    return item.children.some((c) => canAccess(c.path));
  }
  return canAccess(item.path);
};

const Sidebar: React.FC<SidebarProps> = ({ open, onToggle, collapsed }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user, loading } = useAppSelector((state: RootState) => state.auth);
  const { canAccessPage } = useRBAC();

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => ({}));
  useEffect(() => {
    if (location.pathname.startsWith("/marketing")) {
      setOpenGroups((prev) => ({ ...prev, marketing: true }));
    }
  }, [location.pathname]);

  const handleNavigation = (path: string) => {
    if (
      location.pathname.startsWith("/booking-management/create-booking") &&
      (path === "/booking-management" || path === "/dashboard")
    ) {
      clearCreateBookingDraft();
    }
    navigate(path);
    if (isMobile) onToggle();
  };

  const handleLogout = () => {
    if (user?.userid) {
      dispatch(logoutThunk(user.userid.toString()));
    }
    navigate("/login");
  };

  const isActiveRoute = (path: string) => {
    if (path === "/dashboard") {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <Drawer
      variant={isMobile ? "temporary" : "persistent"}
      anchor="left"
      open={open}
      onClose={onToggle}
      sx={{
        width: collapsed ? 80 : 280,
        "& .MuiDrawer-paper": {
          width: collapsed ? 80 : 270,
          boxSizing: "border-box",
          backgroundColor: "#fff",
          borderRight: "1px solid #dee2e6",
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        },
      }}
    >
      <Stack className="h-100" sx={{ pt: "4.5rem" }}>
        <List className="flex-grow-1 px-2 py-2">
          {NAVIGATION_ITEMS.filter((item) => navItemVisible(item, canAccessPage)).map((item) => {
            if (item.children?.length) {
              const childActive = item.children.some(
                (c) =>
                  location.pathname === c.path || location.pathname.startsWith(`${c.path}/`)
              );
              const groupOpen = openGroups[item.id] ?? childActive;
              return (
                <React.Fragment key={item.id}>
                  <ListItem disablePadding className="mb-1">
                    <ListItemButton
                      onClick={() => {
                        if (collapsed) {
                          handleNavigation(item.children![0].path);
                          return;
                        }
                        setOpenGroups((prev) => ({
                          ...prev,
                          [item.id]: !groupOpen,
                        }));
                      }}
                      className="rounded-3 px-2 fw-semibold"
                      sx={{
                        backgroundColor: childActive
                          ? "rgba(22, 67, 135, 0.08)"
                          : undefined,
                        color: childActive ? SIDEBAR_ICON_ACTIVE : "inherit",
                        "&:hover": {
                          backgroundColor: childActive
                            ? "rgba(22, 67, 135, 0.13)"
                            : "rgba(25,118,210,0.05)",
                        },
                        justifyContent: collapsed ? "center" : "flex-start",
                        minHeight: 48,
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: collapsed ? "auto" : 32,
                          color: childActive ? SIDEBAR_ICON_ACTIVE : SIDEBAR_ICON_INACTIVE,
                          justifyContent: "center",
                          mr: collapsed ? 0 : 1,
                        }}
                      >
                        {getIcon(item.icon, childActive)}
                      </ListItemIcon>
                      {!collapsed && (
                        <>
                          <ListItemText
                            primary={
                              <Typography
                                sx={{
                                  fontSize: "0.94rem",
                                  fontWeight: childActive ? 600 : 500,
                                  color: childActive ? SIDEBAR_ICON_ACTIVE : "inherit",
                                }}
                              >
                                {item.label}
                              </Typography>
                            }
                          />
                          {groupOpen ? (
                            <ExpandLess sx={{ color: SIDEBAR_ICON_ACTIVE }} />
                          ) : (
                            <ExpandMore />
                          )}
                        </>
                      )}
                    </ListItemButton>
                  </ListItem>
                  {!collapsed && (
                    <Collapse in={groupOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {item.children
                          .filter((c) => canAccessPage(c.path))
                          .map((child) => {
                            const cActive =
                              location.pathname === child.path ||
                              location.pathname.startsWith(`${child.path}/`);
                            return (
                              <ListItem key={child.id} disablePadding className="mb-1">
                                <ListItemButton
                                  onClick={() => handleNavigation(child.path)}
                                  className="rounded-3 px-2 fw-semibold"
                                  sx={{
                                    pl: 4,
                                    backgroundColor: cActive ? "rgba(22, 67, 135, 0.08)" : undefined,
                                    color: cActive ? "rgba(22, 67, 135, 1)" : "inherit",
                                    "&:hover": {
                                      backgroundColor: cActive
                                        ? "rgba(22, 67, 135, 0.13)"
                                        : "rgba(25,118,210,0.05)",
                                    },
                                    minHeight: 44,
                                  }}
                                >
                                  <ListItemText
                                    primary={
                                      <Typography
                                        sx={{
                                          fontSize: "0.88rem",
                                          fontWeight: cActive ? 600 : 500,
                                          color: cActive ? SIDEBAR_ICON_ACTIVE : "inherit",
                                        }}
                                      >
                                        {child.label}
                                      </Typography>
                                    }
                                  />
                                </ListItemButton>
                              </ListItem>
                            );
                          })}
                      </List>
                    </Collapse>
                  )}
                </React.Fragment>
              );
            }

            const active = isActiveRoute(item.path);
            return (
              <ListItem key={item.id} disablePadding className="mb-1">
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  className="rounded-3 px-2 fw-semibold"
                  sx={{
                    backgroundColor: active ? "rgba(22, 67, 135, 0.08)" : undefined,
                    color: active ? SIDEBAR_ICON_ACTIVE : "inherit",
                    "&:hover": {
                      backgroundColor: active
                        ? "rgba(22, 67, 135, 0.13)"
                        : "rgba(25,118,210,0.05)",
                    },
                    justifyContent: collapsed ? "center" : "flex-start",
                    minHeight: 48,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: collapsed ? "auto" : 32,
                      color: active ? SIDEBAR_ICON_ACTIVE : SIDEBAR_ICON_INACTIVE,
                      justifyContent: "center",
                      mr: collapsed ? 0 : 1,
                    }}
                  >
                    {getIcon(item.icon, active)}
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={
                        <Typography
                          sx={{
                            fontSize: "0.94rem",
                            fontWeight: active ? 600 : 500,
                            color: active ? SIDEBAR_ICON_ACTIVE : "inherit",
                          }}
                        >
                          {item.label}
                        </Typography>
                      }
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        <Box className="border-top bg-light px-3 py-2">
          <Box
            className={`d-flex align-items-center mb-2 rounded-3 px-2 fw-semibold ${
              collapsed ? "justify-content-center" : ""
            }`}
            onClick={() => handleNavigation("/user/profile")}
            sx={{
              cursor: "pointer",
              py: 1.5,
              backgroundColor: isActiveRoute("/user/profile")
                ? "rgba(22, 67, 135, 0.08)"
                : undefined,
              color: isActiveRoute("/user/profile")
                ? SIDEBAR_ICON_ACTIVE
                : "inherit",
              "&:hover": {
                backgroundColor: isActiveRoute("/user/profile")
                  ? "rgba(22, 67, 135, 0.13)"
                  : "rgba(25,118,210,0.05)",
              },
            }}
          >
            <Avatar
              sx={{
                width: 25,
                height: 25,
                backgroundColor: theme.palette.primary.main,
                fontSize: "0.756rem",
                mr: collapsed ? 0 : 1,
              }}
            >
              {user?.name?.[0] || user?.username?.[0] || "U"}
            </Avatar>
            {!collapsed && (
              <Typography
                variant="body1"
                sx={{
                  fontSize: "0.94rem",
                  fontWeight: isActiveRoute("/user/profile") ? 600 : 500,
                }}
              >
                {user?.name}
              </Typography>
            )}
          </Box>

          <ListItemButton
            onClick={handleLogout}
            disabled={loading}
            className={`px-0 text-dark rounded ${
              collapsed ? "justify-content-center" : ""
            }`}
            sx={{
              "&:hover": { backgroundColor: "rgba(25,118,210,0.05)" },
              justifyContent: collapsed ? "center" : "flex-start",
              opacity: loading ? 0.6 : 1,
            }}
          >
            <img
              src={LogoutIcon}
              alt="Logout"
              width={25}
              height={25}
              className={collapsed ? "" : "me-2"}
            />
            {!collapsed && (
              <ListItemText
                primary={
                  <Typography sx={{ fontSize: "0.94rem", fontWeight: 500 }}>
                    {loading ? "Logging out..." : "Logout"}
                  </Typography>
                }
              />
            )}
          </ListItemButton>
        </Box>
      </Stack>
    </Drawer>
  );
};

export default Sidebar;
